import { QueryClient } from '@tanstack/react-query';
import { getAccounts } from '@vindr/services/apis/account.api';
import { getBodyParts } from '@vindr/services/apis/bodyPart.api';
import { getCadRules } from '@vindr/services/apis/cad.api';
import { getDepartments } from '@vindr/services/apis/department.api';
import { getDevices } from '@vindr/services/apis/devices.api';
import { getFiltersManagement } from '@vindr/services/apis/filterManagement.api';
import { getInstitutions } from '@vindr/services/apis/institution.api';
import { getLayouts } from '@vindr/services/apis/layout.api';
import { getOrders } from '@vindr/services/apis/order.api';
import { getPatientsVisited } from '@vindr/services/apis/patient.api';
import { getProcedures, getProceduresGroup } from '@vindr/services/apis/procedure.api';
import { getReports } from '@vindr/services/apis/report.api';
import { getRoles } from '@vindr/services/apis/role.api';
import { ACCESS_TYPE, getShares } from '@vindr/services/apis/share.api';
import { getTemplates } from '@vindr/services/apis/template.api';
import { Share_Type } from '@vindr/services/constants/common.constant';
import { INIT_GET_FILTER } from '@vindr/services/constants/filter.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import keycloak from '@vindr/services/keycloak';

type queryKeyValid = Exclude<
  queryKey,
  | queryKey.userInfo
  | queryKey.cad_result
  | queryKey.filter_tree
  | queryKey.orders_interval
  | queryKey.studies_interval
>;

type prefetchTemplateInterface = {
  [key in queryKeyValid]: {
    queryKey: [queryKey, Record<string, any>];
    queryFn: () => Promise<any>;
    enabled?: boolean;
  };
};

export const prefetchTemplate: prefetchTemplateInterface = {
  [queryKey.account]: {
    queryKey: [queryKey.account, INIT_GET_FILTER],
    queryFn: () => getAccounts(INIT_GET_FILTER),
  },
  [queryKey.cad_rules]: {
    queryKey: [queryKey.cad_rules, INIT_GET_FILTER],
    queryFn: () => getCadRules(INIT_GET_FILTER),
  },
  [queryKey.devices]: {
    queryKey: [queryKey.devices, INIT_GET_FILTER],
    queryFn: () => getDevices(INIT_GET_FILTER),
  },
  [queryKey.filter_management]: {
    queryKey: [queryKey.filter_management, INIT_GET_FILTER],
    queryFn: () => getFiltersManagement(INIT_GET_FILTER),
  },
  [queryKey.orders]: {
    queryKey: [queryKey.orders, INIT_GET_FILTER],
    queryFn: () => getOrders(INIT_GET_FILTER),
  },
  [queryKey.visited_patients]: {
    queryKey: [queryKey.visited_patients, INIT_GET_FILTER],
    queryFn: () => getPatientsVisited(INIT_GET_FILTER),
  },
  [queryKey.procedure]: {
    queryKey: [queryKey.procedure, INIT_GET_FILTER],
    queryFn: () => getProcedures(INIT_GET_FILTER),
  },
  [queryKey.procedure_group]: {
    queryKey: [queryKey.procedure_group, INIT_GET_FILTER],
    queryFn: () => getProceduresGroup(INIT_GET_FILTER),
    enabled: !!keycloak.hasOneOfPerm([PERMISSIONS.page.admin_settings]),
  },
  [queryKey.bodyPart]: {
    queryKey: [queryKey.bodyPart, INIT_GET_FILTER],
    queryFn: () => getBodyParts(INIT_GET_FILTER),
  },
  [queryKey.department]: {
    queryKey: [queryKey.department, INIT_GET_FILTER],
    queryFn: () => getDepartments(INIT_GET_FILTER),
  },
  [queryKey.reports]: {
    queryKey: [queryKey.reports, INIT_GET_FILTER],
    queryFn: () => getReports(INIT_GET_FILTER),
  },
  [queryKey.roles]: {
    queryKey: [queryKey.roles, INIT_GET_FILTER],
    queryFn: () => getRoles(INIT_GET_FILTER),
    enabled: !!keycloak.hasOneOfPerm([PERMISSIONS.page.admin_settings]),
  },
  [queryKey.share]: {
    queryKey: [queryKey.share, { ...INIT_GET_FILTER, type: Share_Type.Guest }],
    queryFn: () =>
      getShares({ ...INIT_GET_FILTER, type: Share_Type.Guest, access_type: ACCESS_TYPE.Study }),
  },
  [queryKey.institution]: {
    queryKey: [queryKey.institution, INIT_GET_FILTER],
    queryFn: () => getInstitutions(INIT_GET_FILTER),
  },
  [queryKey.layout_report]: {
    queryKey: [queryKey.layout_report, INIT_GET_FILTER],
    queryFn: () => getLayouts(INIT_GET_FILTER),
  },
  [queryKey.template_report]: {
    queryKey: [queryKey.template_report, INIT_GET_FILTER],
    queryFn: () => getTemplates(INIT_GET_FILTER),
  },
};

export const getPrefetch = (
  queryClient: QueryClient,
  list: Array<{
    queryKey: (Record<string, any> | queryKey)[];
    queryFn: () => Promise<any>;
    enabled?: boolean;
  }>
) => {
  list.forEach((item) => {
    if (item?.enabled === undefined || item?.enabled === true) {
      queryClient.prefetchQuery(item.queryKey, item.queryFn);
    }
  });
};
